import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StyledMachineRobotCleaningControls, StyledCleaningControlsAlert } from './MachineRobotCleaningControls.styles';
import { useCleaningControlsState } from './hooks/useCleaningControlsState';
import { CleaningCommandErrorAlert } from './components/CleaningCommandErrorAlert/CleaningCommandErrorAlert';
import { ModalMessage, ModalType } from './components/MachineDetailsRobotLiveMapScreen/components/ModalMessage';
import { PAUSE_RESUME_COMMAND_TYPES } from './utils/constants';
import { MachineDetailsRobotLiveMapScreen } from './components/MachineDetailsRobotLiveMapScreen/MachineDetailsRobotLiveMapScreen';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { isRobotCleaning } from 'app/modules/machine-inventory/utils/robotCleaningUtils';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { RobotCleaningControlActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSlice';
import { selectRobotCleaningControlErrorCommandType } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSelectors';
import type { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import type { RobotModelForLiveMap } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/liveMap/liveMapActions.types';

interface MachineRobotCleaningControlsProps {
  isLiveMapOpen: boolean;
  onLiveMap: () => void;
  model: RobotModelForLiveMap;
  robotName: string;
  scheduleName: string;
  mapName: string;
  deviceId: string;
  materialNumber: string;
  serialNumber: string;
  /** B50 only: repeat-path id. */
  routeId?: string;
  robot: Machine;
}

export const MachineRobotCleaningControls = ({
  isLiveMapOpen,
  onLiveMap,
  model,
  robotName,
  scheduleName,
  mapName,
  deviceId,
  materialNumber,
  serialNumber,
  routeId,
  robot,
}: MachineRobotCleaningControlsProps): JSX.Element => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const errorCommandType = useSelector(selectRobotCleaningControlErrorCommandType);

  const {
    isPaused,
    isRoutineRun,
    hasTaskEnded,
    pendingAction,
    isRequestPending,
    isDisabled,
    isBlockedWhilePaused,
    handleSendHome,
    handlePauseOrResume,
    handleCancelCleaning,
    handleSkipRoute,
  } = useCleaningControlsState({
    robotId: deviceId,
    materialNumber,
    serialNumber,
    model,
    isRobotCleaning: isRobotCleaning(robot),
  });

  // Send home and Cancel cleaning ask for confirmation; Pause/Resume act immediately.
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const wasPendingRef = useRef(false);

  const closeModal = (): void => {
    setModalType(null);
    dispatch(RobotCleaningControlActions.dismissCleaningCommandError());
  };

  // The modal stays open while the command runs and after a failure (to show the error / retry);
  // it closes only once the command succeeds.
  const isModalCommandPending =
    isRequestPending && (pendingAction === 'sendHome' || pendingAction === 'cancelCleaning');

  useEffect(() => {
    if (wasPendingRef.current && !isModalCommandPending && !errorCommandType) {
      setModalType(null);
    }

    wasPendingRef.current = isModalCommandPending;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalCommandPending]);

  const openSendHomeModal = (): void => setModalType(ModalType.SEND_HOME);
  const openCancelCleaningModal = (): void => setModalType(ModalType.CANCEL_CLEANING);

  return (
    <>
      <StyledCleaningControlsAlert>
        <CleaningCommandErrorAlert commandTypes={PAUSE_RESUME_COMMAND_TYPES} />
      </StyledCleaningControlsAlert>
      <StyledMachineRobotCleaningControls className="machine-robot-cleaning-controls">
        <KccButton type="primary" className="machine-robot-cleaning-controls__live-map" onClick={onLiveMap}>
          {t('robotCleaningControl.liveMap.label')}
        </KccButton>
        {!hasTaskEnded && (
          <>
            <KccButton
              type="secondary"
              shape="circle"
              aria-label={t('robotCleaningControl.sendHome.label')}
              title={t('robotCleaningControl.sendHome.label')}
              disabled={isDisabled || isBlockedWhilePaused}
              icon={pendingAction === 'sendHome' ? <PendingSpinnerIcon /> : <SvgIcon name="house" size={20} />}
              onClick={openSendHomeModal}
            />
            <KccButton
              type="secondary"
              shape="circle"
              aria-label={t(isPaused ? 'robotCleaningControl.resume.label' : 'robotCleaningControl.pause.label')}
              title={t(isPaused ? 'robotCleaningControl.resume.label' : 'robotCleaningControl.pause.label')}
              disabled={isDisabled}
              icon={
                pendingAction === 'pauseResume' ? (
                  <PendingSpinnerIcon />
                ) : (
                  <SvgIcon name={isPaused ? 'playTight' : 'pauseTight'} size={20} />
                )
              }
              onClick={handlePauseOrResume}
            />
            <KccButton
              type="secondary"
              shape="circle"
              aria-label={t('robotCleaningControl.cancelCleaning.label')}
              title={t('robotCleaningControl.cancelCleaning.label')}
              disabled={isDisabled || isBlockedWhilePaused}
              icon={pendingAction === 'cancelCleaning' ? <PendingSpinnerIcon /> : <SvgIcon name="cancel" size={20} />}
              onClick={openCancelCleaningModal}
            />
          </>
        )}
      </StyledMachineRobotCleaningControls>

      <MachineDetailsRobotLiveMapScreen
        isOpen={isLiveMapOpen}
        onClose={onLiveMap}
        isPaused={isPaused}
        // Skip route moves a routine on to its next route, so it only applies to a B50 routine run.
        showSkipRoute={model === 'B50' && isRoutineRun}
        hasTaskEnded={hasTaskEnded}
        onSkipRoute={handleSkipRoute}
        pendingAction={pendingAction}
        isDisabled={isDisabled}
        isBlockedWhilePaused={isBlockedWhilePaused}
        onSendHome={openSendHomeModal}
        onPauseOrResume={handlePauseOrResume}
        onCancelCleaning={openCancelCleaningModal}
        model={model}
        robotName={robotName}
        scheduleName={scheduleName}
        mapName={mapName}
        deviceId={deviceId}
        materialNumber={materialNumber}
        serialNumber={serialNumber}
        routeId={routeId}
        robot={robot}
      />

      <ModalMessage
        isOpen={modalType !== null}
        isPending={isModalCommandPending}
        onClose={closeModal}
        onConfirm={modalType === ModalType.CANCEL_CLEANING ? handleCancelCleaning : handleSendHome}
        onSecondary={handleSendHome}
        type={modalType ?? ModalType.SEND_HOME}
      />
    </>
  );
};
